/**
 * Application entry point.
 *
 * Runtime config is loaded from /config.json (deployed to S3 by the CDK WebStack).
 * This decouples the build from environment-specific values — the bundle is generic
 * and config is injected at deploy time.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, ThemeProvider, NotificationProvider, ProtectedRoute } from '@amzn/neura-web-shared-components';
import '@cloudscape-design/global-styles/index.css';
import App from './App';
import { initRUM, logError } from './shared/monitoring';
import { setApiBaseUrl } from './api/client';

/** Runtime config shape — matches the config.json written by CDK WebStack */
interface RuntimeConfig {
  environment: string;
  baseUrl: string;
  apiBaseUrl: string;
  cognito: {
    userPoolId: string;
    clientId: string;
    identityPoolId: string;
    domain: string;
  };
  rum?: {
    applicationId: string;
    applicationVersion?: string;
    region?: string;
    guestRoleArn?: string;
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

async function loadConfig(): Promise<RuntimeConfig> {
  const response = await fetch('/config.json');
  if (!response.ok) {
    throw new Error(`Failed to load /config.json: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

async function bootstrap() {
  let config: RuntimeConfig;
  try {
    config = await loadConfig();
  } catch (err) {
    // Show a user-friendly error if config.json is missing (first deploy before WebStack)
    document.getElementById('root')!.innerHTML = `
      <div style="font-family: system-ui; padding: 2rem; text-align: center;">
        <h2>Configuration not available</h2>
        <p>The application configuration has not been deployed yet.</p>
        <p style="color: #666; font-size: 0.9rem;">${err}</p>
      </div>
    `;
    return;
  }

  window.addEventListener('error', (e) => logError(e.error ?? new Error(e.message)));
  window.addEventListener('unhandledrejection', (e) => logError(new Error(String(e.reason))));

  // Set page title with environment name for non-prod stages
  const env = config.environment?.toLowerCase();
  if (env && env !== 'prod' && env !== 'production') {
    document.title = `Neura Knowledge Management [${config.environment}]`;
  }

  // Configure API client with runtime base URL
  setApiBaseUrl(config.apiBaseUrl);

  // CloudWatch RUM (only when RUM config is available)
  if (config.rum?.applicationId) {
    initRUM({
      applicationId: config.rum.applicationId,
      applicationVersion: config.rum?.applicationVersion ?? '1.0.0',
      applicationRegion: config.rum?.region ?? 'us-east-1',
      identityPoolId: config.cognito.identityPoolId,
      endpoint: `https://dataplane.rum.${config.rum?.region ?? 'us-east-1'}.amazonaws.com`,
      guestRoleArn: config.rum?.guestRoleArn ?? '',
    });
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <BrowserRouter>
        <AuthProvider cognitoConfig={config.cognito}>
          <ProtectedRoute>
            <ThemeProvider>
              <NotificationProvider>
                <QueryClientProvider client={queryClient}>
                  <App />
                </QueryClientProvider>
              </NotificationProvider>
            </ThemeProvider>
          </ProtectedRoute>
        </AuthProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
}

bootstrap();
